import { IResource, PickAsPartial } from '@wyz/types';
import { isNil, isObject } from 'lodash-es';

import React, { useEffect, useState } from 'react';

type PictureSrc =
  | string
  | ({ id: string } & PickAsPartial<IResource, 'resourcesFormat'>)
  | undefined
  | null;
const resolveImgSrc = async ({
  baseUrl,
  src,
  defaultImage,
  pathResolver,
  token,
}: {
  src?: PictureSrc;
  baseUrl?: string;
  defaultImage: string;
  pathResolver: (key: string) => string;
  token?: string;
}): Promise<string | undefined> => {
  if (isNil(src)) {
    return defaultImage;
  } else if (isObject(src)) {
    if (src.resourcesFormat) {
      return src.resourcesFormat.find((format) => format.size === 'original')
        ?.url;
    }
    const path = baseUrl
      ? `${baseUrl}${pathResolver(src.id)}`
      : pathResolver(src.id);
    const resolved = await fetch(path, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }).then(async (res) => {
      if (!res.ok) {
        throw new Error('Failed to fetch image');
      }
      const json = await res.json();
      return json?.data?.resourcesFormat[0]?.url ?? '-';
    });

    return resolved;
  } else {
    return baseUrl ? `${baseUrl}${src}` : src;
  }
};

export interface ImgProps extends Omit<
  React.HTMLProps<HTMLImageElement>,
  'src'
> {
  'id'?: string;
  'style'?: React.CSSProperties;
  'variant'?: 'no-radius' | 'rounded' | 'rounded-20';
  'getToken'?: () => string;
  'loader'?: React.ComponentType<{
    width?: string | number;
    height?: string | number;
  }>;
  'src'?: PictureSrc;
  'defaultImage': string;
  'errorFallback'?: React.ReactNode;
  'className'?: string;
  'baseUrl'?: string;
  'alt'?: string;
  'width'?: number | string;
  'height'?: number | string;
  'pathResolver'?: (key: string) => string;
  'data-cy'?: string;
}

const Img = ({
  id,
  loader,
  defaultImage,
  getToken,
  width,
  height,
  src,
  alt,
  pathResolver,
  baseUrl,
  errorFallback,
  ...rest
}: ImgProps) => {
  const DEFAULT_RESOLVER = (key: string) =>
    getToken ? `/tenants/api/resources/${key}` : '';
  const getPath = pathResolver || DEFAULT_RESOLVER;
  const Loader = loader;
  const [imageSrc, setImageSrc] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const onLoad = () => {
    setLoaded(true);
  };

  const onError = () => {
    setErrored(true);
    setImageSrc(defaultImage);
    setLoaded(true);
  };

  useEffect(() => {
    setErrored(false);
    resolveImgSrc({
      src,
      baseUrl,
      defaultImage,
      pathResolver: getPath,
      token: getToken?.(),
    })
      .then((resolvedSrc) => {
        if (resolvedSrc) {
          setImageSrc(resolvedSrc);
        } else {
          setImageSrc(defaultImage);
        }
      })
      .catch(() => {
        setImageSrc(defaultImage);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, baseUrl]);

  if (errored && errorFallback != null) {
    return <>{errorFallback}</>;
  }

  return (
    <>
      {!loaded && Loader && <Loader width={width} height={height} />}
      <img
        src={imageSrc || undefined}
        {...rest}
        alt={alt}
        onLoad={onLoad}
        onError={onError}
        style={{
          ...rest.style,
          visibility: loaded ? 'visible' : 'hidden',
          height: loaded ? height : 0,
          width: loaded ? width : 0,
          maxWidth: loaded ? rest.style?.maxWidth : 0,
          maxHeight: loaded ? rest.style?.maxHeight : 0,
        }}
        loading='lazy'
        data-cy={rest['data-cy'] ?? id}
        data-testid={id}
      />
    </>
  );
};

export default Img;
